import * as Sentry from "@sentry/browser";
import {useObjectVal} from "react-firebase-hooks/database";
import {FontDefinition} from "data/models/shared/recommended-fonts";
import {ref, set} from "firebase/database";
import {database} from "hooks/firebase";
import {SelectedColors} from "data/models/survey/colors";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {useRecommendedFonts} from "data/access/survey-question-pages/fonts";
import {useCallback, useEffect, useMemo, useState} from "react";
import {defaultFonts} from "data/definition/defaults/fonts";
import {defaultBrandColors} from "data/definition/defaults/colors";
import {useActivePurchasedProduct} from "data/access/purchase/useActivePurchasedProduct";
import {BrandStylesResult} from "data/models/deliverables/brand-styles";
import {deepEqual} from "helpers/deep-equal";

export const UNDEFINED_BRAND_STYLES_RESULT: BrandStylesResult = {
  brandColors: defaultBrandColors,
  brandFont: defaultFonts.brandFont,
  subtitleFont: defaultFonts.headlineFont,
  headingFont: defaultFonts.brandFont,
  subheadingFont: defaultFonts.headlineFont,
  sectionHeaderFont: defaultFonts.headlineFont,
  bodyFont: defaultFonts.bodyFont,
  quoteFont: defaultFonts.quoteFont,
  captionFont: defaultFonts.captionFont,
}

type BrandStylesType = {
  brandStyles: BrandStylesResult | null;
  availableCreditsToUse: number;
  canGenerateNew: boolean;
}

export function useBrandStyles(): [() => Promise<void>, BrandStylesType, boolean, Error | null] {
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const visualStylePath = useMemo(() => `user-data/${user?.uid ?? ""}/user-surveys/canva-assets/all-features/visual-recommendations`, [user]);
  const [brandFont, brandFontLoading, brandFontError] =
      useObjectVal<FontDefinition>((user && ref(database, `${visualStylePath}/choose-brand-font`)) ?? null);
  const [brandColors, brandColorsLoading, brandColorsError] =
      useObjectVal<SelectedColors>((user && ref(database, `${visualStylePath}/choose-brand-color`)) ?? null);

  const brandStylesDeliverablePath = useMemo(() => `user-data/${user?.uid ?? ""}/deliverables/brand-styles`, [user]);
  const [savedBrandStyles, savedBrandStylesLoading, savedBrandStylesError] =
      useObjectVal<BrandStylesResult>((user && ref(database, brandStylesDeliverablePath)) ?? null);

  const [recommendedFonts, recommendedFontsLoading, recommendedFontsError] = useRecommendedFonts();

  const anyUndefined = (
      isNullOrUndefined(user)
      || isNullOrUndefined(brandFont)
      || isNullOrUndefined(brandColors)
      || isNullOrUndefined(recommendedFonts)
  );

  const newBrandStylesValue = useMemo((): BrandStylesResult | null => (!anyUndefined ? {
    brandColors,
    brandFont,
    subtitleFont: recommendedFonts.headline,
    headingFont: brandFont,
    subheadingFont: recommendedFonts.headline,
    sectionHeaderFont: recommendedFonts.headline,
    bodyFont: recommendedFonts.body,
    quoteFont: {
      family: `${brandFont.name}:400`,
      name: brandFont.name,
      weight: 400,
      italic: brandFont.italic ?? false,
    },
    captionFont: recommendedFonts.caption,
  } : null), [anyUndefined, brandColors, brandFont, recommendedFonts]);

  const areStylesEqual = useMemo(() => deepEqual(newBrandStylesValue, savedBrandStyles ?? null), [savedBrandStyles, newBrandStylesValue])

  const [generating, setGenerating] = useState<boolean>(false);

  useEffect(() => {
    if (areStylesEqual && generating) {
      setGenerating(false);
    }
  }, [areStylesEqual, generating]);

  const [purchasedProduct, purchasedProductLoading, purchasedProductError] = useActivePurchasedProduct('bc-express-annual');

  const anyLoading = (
      brandFontLoading
      || brandColorsLoading
      || savedBrandStylesLoading
      || recommendedFontsLoading
      || purchasedProductLoading
  );

  const anError = (
      brandFontError
      ?? brandColorsError
      ?? savedBrandStylesError
      ?? recommendedFontsError
      ?? purchasedProductError
      ?? null
  );

  const readyToGenerate = (
      !anyLoading
      && !generating
      && anError === null
      && !anyUndefined
      && newBrandStylesValue !== null
      && !isNullOrUndefined(user)
  );

  const generateBrandStyles = useCallback(async (): Promise<void> => {
    if (!readyToGenerate) {
      return
    }
    setGenerating(true);
    await set(ref(database, brandStylesDeliverablePath), newBrandStylesValue)
        .catch(error => Sentry.captureException(error));
  }, [
    readyToGenerate,
    brandStylesDeliverablePath,
    newBrandStylesValue,
  ]);

  return [
    generateBrandStyles,
    {
      brandStyles: savedBrandStyles ?? null,
      availableCreditsToUse: purchasedProduct !== null ? 1 : 0,
      canGenerateNew: readyToGenerate && !areStylesEqual,
    }, anyLoading || generating, anError];
}
